/**
 * Image validation and sanitization utilities for PDF export
 */

export const validateBase64Image = (base64Data: string): boolean => {
  try {
    // Check if it's a valid base64 string
    if (!base64Data || typeof base64Data !== 'string') return false;

    // Remove data URL prefix if present
    const cleanBase64 = base64Data.replace(/^data:image\/[a-zA-Z]+;base64,/, '');

    // Check if remaining string is valid base64
    const base64Regex = /^[A-Za-z0-9+/]*(=*)$/;
    if (!base64Regex.test(cleanBase64)) return false;

    // Check length (base64 should be divisible by 4, accounting for padding)
    const paddingLength = (cleanBase64.match(/=/g) || []).length;
    if (paddingLength > 2) return false;

    // Check if it's not too large (5MB limit for base64)
    const sizeInBytes = (cleanBase64.length * 3) / 4 - paddingLength;
    if (sizeInBytes > 5 * 1024 * 1024) return false;

    return true;
  } catch (error) {
    console.warn('Base64 validation error:', error);
    return false;
  }
};

export const sanitizeBase64Image = (base64Data: string): string | null => {
  try {
    if (!base64Data || typeof base64Data !== 'string') return null;

    // Extract the base64 part
    const cleanBase64 = base64Data.replace(/^data:image\/[a-zA-Z]+;base64,/, '');

    // Remove invalid characters
    const sanitized = cleanBase64.replace(/[^A-Za-z0-9+/=]/g, '');

    // Validate the sanitized version
    if (!validateBase64Image(sanitized)) return null;

    return sanitized;
  } catch (error) {
    console.warn('Base64 sanitization error:', error);
    return null;
  }
};

export const createSafeDataUrl = (imageData: string, mimeType: string = 'jpeg'): string => {
  try {
    // If already a data URL, validate it
    if (imageData.startsWith('data:image/')) {
      const isValid = validateBase64Image(imageData);
      return isValid ? imageData : createPlaceholderDataUrl();
    }

    // Sanitize base64 data
    const sanitizedBase64 = sanitizeBase64Image(imageData);
    if (!sanitizedBase64) {
      return createPlaceholderDataUrl();
    }

    return `data:image/${mimeType};base64,${sanitizedBase64}`;
  } catch (error) {
    console.warn('Error creating safe data URL:', error);
    return createPlaceholderDataUrl();
  }
};

export const createPlaceholderDataUrl = (): string => {
  // Small 1x1 transparent PNG as fallback
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
};

export const compressBase64Image = async (
  base64Data: string,
  maxSizeKB: number = 500,
): Promise<string> => {
  return new Promise((resolve) => {
    try {
      // If already small enough, return as-is
      const currentSize = (base64Data.length * 3) / 4 / 1024; // Size in KB
      if (currentSize <= maxSizeKB) {
        resolve(base64Data);
        return;
      }

      // Create canvas for compression
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions to reduce file size
          const scaleFactor = Math.sqrt(maxSizeKB / currentSize);
          canvas.width = Math.max(1, Math.floor(img.width * scaleFactor));
          canvas.height = Math.max(1, Math.floor(img.height * scaleFactor));

          // Draw and compress
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressedDataUrl);
        } catch (error) {
          console.warn('Image compression error:', error);
          resolve(createPlaceholderDataUrl());
        }
      };

      img.onerror = () => {
        console.warn('Failed to load image for compression');
        resolve(createPlaceholderDataUrl());
      };

      // Set source with proper data URL format
      img.src = base64Data.startsWith('data:')
        ? base64Data
        : `data:image/jpeg;base64,${base64Data}`;
    } catch (error) {
      console.warn('Compression setup error:', error);
      resolve(createPlaceholderDataUrl());
    }
  });
};
